import { TFunction } from 'i18next'

interface TranslatePropsOptions {
    language: string
}

/**
 * Recursively translates properties in an object based on language context
 * @param props - The props object to translate
 * @param options - Translation options including language
 * @returns The translated props object
 */
export function translateProps<T extends Record<string, any>>(props: T, options: TranslatePropsOptions): T {
    if (!props || typeof props !== 'object') {
        return props
    }

    const { language } = options
    const result = { ...props }

    // Recursively process each property
    for (const key in result) {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
            const value = result[key]

            // Handle arrays
            if (Array.isArray(value)) {
                result[key] = value.map((item: any) =>
                    typeof item === 'object' ? translateProps(item, options) : item
                )
                continue
            }

            // Handle nested objects
            if (value && typeof value === 'object') {
                result[key] = translateProps(value, options)
                continue
            }

            // Handle translation swapping for Arabic
            if (language === 'ar') {
                const baseKey = key.replace('_ar', '')
                const arKey = `${baseKey}_ar`

                // If this is a base key and has an Arabic version, skip
                // (we'll handle it when we process the _ar key)
                if (key === baseKey && result[arKey]) {
                    continue
                }

                // If this is an Arabic key, swap it with the base key
                if (key.endsWith('_ar')) {
                    const value = result[key]
                    if (value !== undefined && value !== '') {
                        result[baseKey] = value
                    }
                }
            }
        }
    }

    // Set direction based on language
    if ('direction' in result) {
        result.direction = language === 'ar' ? 'rtl' : 'ltr'
    }

    return result
}

/**
 * Hook-friendly wrapper for translateProps
 * @param props - The props object to translate
 * @param language - Current language code
 * @returns Translated props
 */
export function useTranslatedProps<T extends Record<string, any>>(props: T, language: string): T {
    return translateProps(props, { language })
}
